import { useEffect, useState } from 'react';
import {
  Dialog,
  Input,
  Select,
  SelectOption,
  TovButton,
  TovField,
  msg,
  useToast,
} from '@tov-plus/ui';

import { guestApi } from '../app/api';
import { guestEnv } from '../config/env';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { uploadToSignedUrl } from '../lib/uploads/uploadToSignedUrl';
import type { GuestGalleryData } from './galleryHelpers';

const uploadErrorMessage = msg(
  'pages.gallery_page.upload_failed_check_connection_and_try_again',
);

interface GalleryUploadDialogProps {
  occasionSlug: string;
  guestToken: string;
  uploadOptions: GuestGalleryData['uploadOptions'];
  uploadConstraints: GuestGalleryData['uploadConstraints'];
  onUploaded: () => Promise<void> | void;
}

export function GalleryUploadDialog({
  occasionSlug,
  guestToken,
  uploadOptions,
  uploadConstraints,
  onUploaded,
}: GalleryUploadDialogProps) {
  const notifications = useToast();
  const [open, setOpen] = useState(false);
  const [uploadOptionId, setUploadOptionId] = useState(
    uploadOptions[0]?.albumId ?? uploadOptions[0]?.eventId ?? '',
  );
  const [caption, setCaption] = useState(
    msg('pages.gallery_page.shared_from_the_dance_floor'),
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!uploadOptionId && uploadOptions[0]) {
      setUploadOptionId(uploadOptions[0].albumId ?? uploadOptions[0].eventId);
    }
  }, [uploadOptionId, uploadOptions]);

  useEffect(() => {
    if (!selectedFile || !selectedFile.type.startsWith('image/')) {
      setPreviewUrl(null);
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(nextPreviewUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewUrl);
    };
  }, [selectedFile]);

  const acceptAttribute = uploadConstraints.acceptedMimePrefixes
    .map((prefix) => `${prefix}*`)
    .join(',');
  const maxFileSizeMb = Math.round(
    uploadConstraints.maxFileSizeBytes / 1024 / 1024,
  );

  async function handleUpload() {
    const uploadOption =
      uploadOptions.find(
        (option) => (option.albumId ?? option.eventId) === uploadOptionId,
      ) ?? null;

    if (!uploadOption) {
      notifications.error(
        msg('pages.gallery_page.choose_event_before_uploading'),
      );
      return;
    }

    if (!selectedFile) {
      notifications.error(
        msg('pages.gallery_page.choose_file_before_uploading'),
      );
      return;
    }

    const matchesAllowedMime = uploadConstraints.acceptedMimePrefixes.some(
      (prefix) => selectedFile.type.startsWith(prefix),
    );

    if (!matchesAllowedMime) {
      notifications.error(
        msg('pages.gallery_page.choose_supported_image_or_video_file'),
      );
      return;
    }

    if (selectedFile.size > uploadConstraints.maxFileSizeBytes) {
      notifications.error(
        msg('pages.gallery_page.selected_file_larger_than_upload_limit'),
      );
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const session = await guestApi.requestGalleryUploadUrl(
        occasionSlug,
        guestToken,
        {
          eventId: uploadOption.eventId,
          albumId: uploadOption.albumId,
          fileName: selectedFile.name,
          mimeType: selectedFile.type || 'application/octet-stream',
          fileSizeBytes: selectedFile.size,
          caption,
        },
      );
      await uploadToSignedUrl({
        url: new URL(session.signedUploadUrl, guestEnv.apiBaseUrl).toString(),
        file: selectedFile,
        headers: session.uploadHeaders,
        contentType: selectedFile.type || 'application/octet-stream',
        onProgress: setUploadProgress,
      });
      const created = await guestApi.completeGalleryUpload(
        occasionSlug,
        guestToken,
        session.id,
      );
      notifications.success(
        created.galleryItem.moderationStatus === 'approved'
          ? msg('pages.gallery_page.photo_shared_successfully')
          : msg(
              'pages.gallery_page.upload_received_waiting_for_planner_approval',
            ),
      );
      await onUploaded();
      setSelectedFile(null);
      setCaption(msg('pages.gallery_page.shared_from_the_dance_floor'));
      setOpen(false);
    } catch (error) {
      notifications.error(getGuestErrorMessage(error, uploadErrorMessage));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isUploading) {
          setOpen(nextOpen);
        }
      }}
      title={msg('pages.gallery_page.pages_gallery_page_share_a_photo')}
      trigger={
        <TovButton>{msg('pages.gallery_page.gallery_add_photos')}</TovButton>
      }
      footer={
        <TovButton
          onClick={handleUpload}
          disabled={isUploading || !selectedFile}
        >
          {isUploading
            ? msg('pages.gallery_page.pages_gallery_page_uploading')
            : msg('general.actions.upload')}
        </TovButton>
      }
    >
      <div className="gallery-upload-form">
        <TovField label={msg('general.modules.gallery.name')}>
          <Select
            value={uploadOptionId}
            onChange={(event) => setUploadOptionId(event.target.value)}
          >
            <SelectOption value="">
              {`${msg('general.actions.choose')} ${msg('general.modules.gallery.name')}`}
            </SelectOption>
            {uploadOptions.map((option) => (
              <SelectOption
                key={option.albumId ?? option.eventId}
                value={option.albumId ?? option.eventId}
              >
                {option.displayName}
              </SelectOption>
            ))}
          </Select>
        </TovField>

        <TovField
          label={msg('pages.gallery_page.pages_gallery_page_photo_or_video')}
        >
          <Input
            accept={acceptAttribute}
            onChange={(event) => {
              setSelectedFile(event.target.files?.[0] ?? null);
              setUploadProgress(0);
            }}
            type="file"
          />
        </TovField>

        {previewUrl ? (
          <div className="gallery-upload-form__preview">
            <img
              alt={msg(
                'pages.gallery_page.pages_gallery_page_selected_upload_preview',
              )}
              src={previewUrl}
            />
          </div>
        ) : null}

        <TovField label={msg('pages.gallery_page.pages_gallery_page_caption')}>
          <Input
            disabled={isUploading}
            onChange={(event) => setCaption(event.target.value)}
            value={caption}
          />
        </TovField>

        <p className="gallery-upload-form__hint">
          {msg('components.dropzone.accepts')}{' '}
          {uploadConstraints.acceptedMimePrefixes.join(', ')}{' '}
          {msg('pages.gallery_page.pages_gallery_page_up_to')} {maxFileSizeMb}
          {msg(
            'pages.gallery_page.pages_gallery_page_mb_uploads_are_delivered_through_optimized_gallery_variants',
          )}
        </p>

        {isUploading ? (
          <p className="gallery-upload-form__progress">
            {msg('pages.gallery_page.pages_gallery_page_upload_progress')}{' '}
            {uploadProgress}%
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
